import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

const SEO = ({ 
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  noindex = false,
  nofollow = false,
  structuredData
}) => {
  const { language } = useLanguage();
  
  // Get settings from localStorage with language support
  const siteTitleID = localStorage.getItem('seo_site_title_id') || 'Manalogi - Partner Transformasi Digital';
  const siteTitleEN = localStorage.getItem('seo_site_title_en') || 'Manalogi - Digital Transformation Partner';
  const siteDescriptionID = localStorage.getItem('seo_site_description_id') || 'Partner setia dalam transformasi digital yang efektif untuk bisnis Anda';
  const siteDescriptionEN = localStorage.getItem('seo_site_description_en') || 'Your trusted partner in effective digital transformation for your business';
  const siteKeywords = localStorage.getItem('seo_site_keywords') || 'digital transformation, software development, IT solutions, BFSI solutions, system integration, Indonesia';
  const siteUrl = localStorage.getItem('seo_site_url') || window.location.origin;
  const ogImageUrl = localStorage.getItem('seo_og_image') || `https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=630&q=80&fit=crop`;
  const twitterHandle = localStorage.getItem('seo_twitter_handle') || '@manalogi';
  
  // Choose language-specific content
  const siteTitle = language === 'id' ? siteTitleID : siteTitleEN;
  const siteDescription = language === 'id' ? siteDescriptionID : siteDescriptionEN;
  
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const metaDescription = description || siteDescription;
  const metaKeywords = keywords || siteKeywords;
  const canonicalUrl = canonical || window.location.href;
  const metaOgImage = ogImage || ogImageUrl;
  
  // Robots meta
  const robots = [];
  if (noindex) robots.push('noindex');
  if (nofollow) robots.push('nofollow');
  const robotsContent = robots.length > 0 ? robots.join(', ') : 'index, follow';
  
  // Manually update meta tags for better compatibility
  useEffect(() => {
    // Update HTML lang
    document.documentElement.lang = language === 'id' ? 'id' : 'en';
    
    // Helper function to set or update meta tag
    const setMetaTag = (selector, attribute, attrValue, content) => {
      let meta = document.querySelector(selector);
      if (meta) {
        meta.setAttribute('content', content);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, attrValue);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };
    
    // Update basic meta tags
    setMetaTag('meta[name="description"]', 'name', 'description', metaDescription);
    setMetaTag('meta[name="keywords"]', 'name', 'keywords', metaKeywords);
    setMetaTag('meta[name="robots"]', 'name', 'robots', robotsContent);
    
    // Update Open Graph tags
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', metaDescription);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', metaOgImage);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', 'Manalogi');
    setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', language === 'id' ? 'id_ID' : 'en_US');
    
    // Update Twitter tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', twitterCard);
    setMetaTag('meta[name="twitter:url"]', 'name', 'twitter:url', canonicalUrl);
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', metaDescription);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', metaOgImage);
    
    // Update or create canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', canonicalUrl);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonical);
    }
    
    // Add structured data if provided
    if (structuredData) {
      // Remove existing structured data for this page
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-react-helmet="true"]');
      existingScripts.forEach(script => script.remove());
      
      // Add new structured data
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-react-helmet', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [language, fullTitle, metaDescription, metaKeywords, canonicalUrl, metaOgImage, ogType, twitterCard, robotsContent, structuredData]);
  
  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={language === 'id' ? 'id' : 'en'} />
      
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaOgImage} />
      <meta property="og:site_name" content="Manalogi" />
      <meta property="og:locale" content={language === 'id' ? 'id_ID' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'id' ? 'en_US' : 'id_ID'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaOgImage} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:site" content={twitterHandle} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="PT Manunggal Laju Teknologi" />
      <meta name="copyright" content="PT Manunggal Laju Teknologi" />
      <meta name="language" content={language === 'id' ? 'Indonesian' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="geo.region" content="ID" />
      <meta name="geo.placename" content="Indonesia" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
